import { queryClient } from '@/utils/query-client.ts'
import { createFileRoute, notFound, Outlet } from '@tanstack/react-router'
import { PageLoadingSpinner } from '@/components/PageLoadingSpinner/PageLoadingSpinner.tsx'
import { CompanyProvider, type CompanyState } from '@/contexts/CompaniesContext'
// eslint-disable-next-line no-restricted-imports
import {
  companiesQueryOptions,
  sitesQueryOptions
} from '@/features/companies/api/companies.api.ts'

import { companyTokenQueryOptions } from '@/features/companies/hooks/useCompanyToken.ts'
import { setTokenProviders } from '@/utils/axios-client'

// At the top of $companySlug.tsx
let previousCompanySlug: string | null = null

export const Route = createFileRoute('/_authenticated/$companySlug')({
  beforeLoad: async ({ params, context }) => {
    const companies = await queryClient.fetchQuery(
      companiesQueryOptions(context.auth.getGlobalToken())
    )

    if (!companies || companies.length === 0) {
      throw notFound({ data: `No companies available` })
    }

    const company = companies.find(c => c.slug === params.companySlug)

    if (!company) {
      throw notFound({ data: `Company ${params.companySlug} not found` })
    }

    const getCompanyToken = () =>
      queryClient
        .fetchQuery(companyTokenQueryOptions(company.id, context.auth.getGlobalToken))
        .then(response => response.accessToken)

    // Set company token provider for axios so all subsequent calls use it
    setTokenProviders({
      getCompanyToken
    })

    // Detect company change and reset queries
    if (previousCompanySlug && previousCompanySlug !== params.companySlug) {
      queryClient.removeQueries({
        predicate: query => query.queryKey.at(0) !== '_global'
      })
    }

    let companyState: CompanyState
    try {
      const sites = await queryClient.fetchQuery(sitesQueryOptions())

      // Update for next navigation
      previousCompanySlug = params.companySlug
      // Build company state with helper methods
      companyState = {
        ...company,
        sites,
        hasSite: (siteId: string) => sites.some(s => s.id.toString() === siteId)
      }
    } catch (e) {
      console.error('Sites failed', e)
      throw notFound({
        data: `Sites for company ${params.companySlug} not found`
      })
    }

    // Return the selected company in context for child routes
    return {
      company: companyState
    }
  },
  pendingComponent: Loader,
  component: CompanyLayout
})

function Loader() {
  // const { t } = useTranslation('global')
  // TODO: Translate when alex is done

  return <PageLoadingSpinner key="page-loader" title="Loading company..." />
}

function CompanyLayout() {
  const { company } = Route.useRouteContext()

  if (!company) {
    // Ok, when we change the search params, the beforeLoad is called (fine), but not the loader (so this component is rendered too soon without the context properly loaded)
    return Loader()
  }

  return (
    <CompanyProvider company={company}>
      <Outlet />
    </CompanyProvider>
  )
}
